import {call, select, put} from "redux-saga/effects";
import { saveExportRequest, 
  saveMeasureExportRequest,
   saveExportSmartTrackingRequest, 
   saveExportExcelRequest, exportFileDownloadRequest
  } from "../requests/exportRequests";
import { setExportLoadingAction } from "../../slices/export";

// Helper function for delay
const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

// Helper function to handle file download from response (JSON with URL or direct blob)
function* handleFileDownload(response, fileName, loadingType, setExportLoadingAction) {
  let downloadUrl = null;
  let blobData = null;
  const contentType = response.headers['content-type'] || response.headers['Content-Type'] || '';
  
  if (response.data instanceof Blob) {
    const blobSize = response.data.size;
    console.log('Blob size:', blobSize, 'bytes (', (blobSize / 1024 / 1024).toFixed(2), 'MB)');
    console.log('Content-Type:', contentType);
    
    // If content-type is JSON, it's a JSON response wrapped in a blob
    if (contentType.includes('application/json') || blobSize < 10000) {
      // Parse JSON from blob
      const text = yield call(() => response.data.text());
      const jsonData = JSON.parse(text);
      console.log('Parsed JSON response:', jsonData);
      
      if (jsonData.url) {
        downloadUrl = jsonData.url;
      } else if (jsonData.download_url) {
        downloadUrl = jsonData.download_url;
      } else if (jsonData.file_url) {
        downloadUrl = jsonData.file_url;
      } else {
        yield put(setExportLoadingAction({ type: loadingType, loading: false, message: null }));
        console.error('No URL found in JSON response:', jsonData);
        return false;
      }
    } else {
      // It's actually a file blob
      if (blobSize === 0) {
        yield put(setExportLoadingAction({ type: loadingType, loading: false, message: null }));
        console.error('Received empty blob');
        return false;
      }
      blobData = response.data;
    }
  } else if (typeof response.data === 'object' && response.data !== null) {
    // Response is already parsed JSON object
    console.log('JSON response:', response.data);
    if (response.data.url) {
      downloadUrl = response.data.url;
    } else if (response.data.download_url) {
      downloadUrl = response.data.download_url;
    } else if (response.data.file_url) {
      downloadUrl = response.data.file_url;
    } else {
      yield put(setExportLoadingAction({ type: loadingType, loading: false, message: null }));
      console.error('No URL found in response:', response.data);
      return false;
    }
  } else if (typeof response.data === 'string' && response.data.startsWith('http')) {
    downloadUrl = response.data;
  } else {
    yield put(setExportLoadingAction({ type: loadingType, loading: false, message: null }));
    console.error('Unexpected response format:', typeof response.data, response.data);
    return false;
  }
  
  // If we have blob data directly, download it
  if (blobData) {
    const url = window.URL.createObjectURL(blobData);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName);
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    
    setTimeout(() => {
      if (link.parentNode) {
        document.body.removeChild(link);
      }
      setTimeout(() => {
        try {
          window.URL.revokeObjectURL(url);
        } catch (e) {
          // Ignore errors
        }
      }, 5000);
    }, 200);
    
    yield put(setExportLoadingAction({ type: loadingType, loading: false, message: null }));
    return true;
  }
  
  // If we have a download URL, use it directly (for pre-signed URLs like DigitalOcean Spaces)
  if (downloadUrl) {
    console.log('Using download URL directly:', downloadUrl);
    
    // For pre-signed URLs (like DigitalOcean Spaces), we can't fetch them due to CORS
    // Instead, we'll use the URL directly in a download link
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.setAttribute('download', fileName);
    link.setAttribute('target', '_blank'); // Open in new tab as fallback
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    
    // Clean up after a delay
    setTimeout(() => {
      if (link.parentNode) {
        document.body.removeChild(link);
      }
    }, 200);
    
    yield put(setExportLoadingAction({ type: loadingType, loading: false, message: null }));
    return true;
  }
  
  return false;
}

export function* saveOrthoExportHandler() {
  try {
    // Set loading state immediately and wait a bit to ensure it propagates
    yield put(setExportLoadingAction({ type: 'ortho', loading: true, message: 'Preparing download...' }));
    yield call(delay, 50); // Small delay to ensure state propagates
    
    const flightSelected = yield select(state => state.flightSelected);
    if (!flightSelected || !flightSelected.uuid) {
      yield put(setExportLoadingAction({ type: 'ortho', loading: false, message: null }));
      console.error('No flight selected');
      return;
    }
    
    const flight_uuid = flightSelected.uuid;
    const flightName = flightSelected.name;
    const projectName = yield select(state => state.selectedProject.name);
    let fileName = `${projectName}-${flightName}-ortho`;
    fileName = fileName.replace(/_+/g, '_');
    
    yield put(setExportLoadingAction({ type: 'ortho', loading: true, message: 'Requesting download URL...' }));
    
    yield put(setExportLoadingAction({ type: 'ortho', loading: true, message: 'Downloading file... This may take a few minutes.' }));
    
    const response = yield call(exportFileDownloadRequest, {flight_uuid: flight_uuid, type: 'ortho' });
    
    console.log('Export response:', response); // Debug log
    console.log('Response status:', response?.status);
    console.log('Response headers:', response?.headers);
    
    if (!response || !response.data) {
      yield put(setExportLoadingAction({ type: 'ortho', loading: false, message: null }));
      console.error('Invalid response from server:', response);
      return;
    }
    
    // Verify response is successful
    if (response.status !== 200) {
      yield put(setExportLoadingAction({ type: 'ortho', loading: false, message: null }));
      console.error('Non-200 response status:', response.status);
      return;
    }
    
    // Handle file download (JSON with URL or direct blob)
    yield* handleFileDownload(response, `${fileName}.tif`, 'ortho', setExportLoadingAction);
  } catch (error) {
    console.error('Error in saveOrthoExportHandler:', error);
    yield put(setExportLoadingAction({ type: 'ortho', loading: false, message: null }));
  }
}

export function* saveDSMExportHandler() {
  try {
    yield put(setExportLoadingAction({ type: 'surfaceModel', loading: true, message: 'Preparing download...' }));
    yield call(delay, 50);
    
    const flightSelected = yield select(state => state.flightSelected);
    if (!flightSelected || !flightSelected.uuid) {
      yield put(setExportLoadingAction({ type: 'surfaceModel', loading: false, message: null }));
      console.error('No flight selected');
      return;
    }
    
    const flight_uuid = flightSelected.uuid;
    const flightName = flightSelected.name;
    const projectName = yield select(state => state.selectedProject.name);
    let fileName = `${projectName}${flightName}elevation`;
    fileName = fileName.replace(/[\s-]/g, '_');
    
    yield put(setExportLoadingAction({ type: 'surfaceModel', loading: true, message: 'Downloading file... This may take a few minutes.' }));
    
    const response = yield call(exportFileDownloadRequest, {flight_uuid: flight_uuid, type: 'elevation' });
    
    console.log('Export response:', response);
    console.log('Response status:', response?.status);
    console.log('Response headers:', response?.headers);
    
    if (!response || !response.data) {
      yield put(setExportLoadingAction({ type: 'surfaceModel', loading: false, message: null }));
      console.error('Invalid response from server:', response);
      return;
    }
    
    // Verify response is successful
    if (response.status !== 200) {
      yield put(setExportLoadingAction({ type: 'surfaceModel', loading: false, message: null }));
      console.error('Non-200 response status:', response.status);
      return;
    }
    
    // Handle file download (JSON with URL or direct blob)
    yield* handleFileDownload(response, `${fileName}.tif`, 'surfaceModel', setExportLoadingAction);
  } catch (error) {
    console.error('Error in saveDSMExportHandler:', error);
    yield put(setExportLoadingAction({ type: 'surfaceModel', loading: false, message: null }));
  }
}


export function* saveTerrainExportHandler() {
  try {
    yield put(setExportLoadingAction({ type: 'hillshade', loading: true, message: 'Preparing download...' }));
    yield call(delay, 50);
    
    const flightSelected = yield select(state => state.flightSelected);
    if (!flightSelected || !flightSelected.uuid) {
      yield put(setExportLoadingAction({ type: 'hillshade', loading: false, message: null }));
      console.error('No flight selected');
      return;
    }
    
    const flight_uuid = flightSelected.uuid;
    const flightName = flightSelected.name;
    const projectName = yield select(state => state.selectedProject.name);
    let fileName = `${projectName}${flightName}hillshade`;
    fileName = fileName.replace(/[\s-]/g, '_');
    
    yield put(setExportLoadingAction({ type: 'hillshade', loading: true, message: 'Downloading file... This may take a few minutes.' }));
    
    const response = yield call(exportFileDownloadRequest, {flight_uuid: flight_uuid, type: 'hillshade' });
    
    console.log('Export response:', response);
    console.log('Response status:', response?.status);
    console.log('Response headers:', response?.headers);
    
    if (!response || !response.data) {
      yield put(setExportLoadingAction({ type: 'hillshade', loading: false, message: null }));
      console.error('Invalid response from server:', response);
      return;
    }
    
    // Verify response is successful
    if (response.status !== 200) {
      yield put(setExportLoadingAction({ type: 'hillshade', loading: false, message: null }));
      console.error('Non-200 response status:', response.status);
      return;
    }
    
    // Handle file download (JSON with URL or direct blob)
    yield* handleFileDownload(response, `${fileName}.tif`, 'hillshade', setExportLoadingAction);
  } catch (error) {
    console.error('Error in saveTerrainExportHandler:', error);
    yield put(setExportLoadingAction({ type: 'hillshade', loading: false, message: null }));
  }
}



export function* saveMeasurementExportHandler() {
  try {
    const flightSelected = yield select(state => state.flightSelected);
    const flightId = flightSelected?.id;
    const flightName = flightSelected?.name;
    const projectName = yield select(state => state.selectedProject.name);
    const fileName = `${projectName}-${flightName}-measurement`;
    const response = yield call(saveMeasureExportRequest, flightId)
    const blob = new Blob([response.data.detail], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    let element = document.createElement('a');
    element.setAttribute('href', url);
    element.setAttribute('download', `${fileName}.kmz`);
    element.style.display = 'none';
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.log(error)
  }
}


export function* saveExportSmartTrackingHandler() {
  try {
    const flightSelected = yield select(state => state.flightSelected);
    const flightId = flightSelected?.id;
    const flightName = flightSelected?.name;
    const projectName = yield select(state => state.selectedProject.name);
    const fileName = `${projectName}-${flightName}-smart-tracking`;
    const response = yield call(saveExportSmartTrackingRequest, flightId);
    if (response?.data?.detail) {
      let element = document.createElement('a');
      let filename = fileName + '.kml'
      element.setAttribute('href', 'data:text/plain;charset=utf-8,' + encodeURIComponent(response?.data?.detail, undefined, 2));
      element.setAttribute('download', filename);

      element.style.display = 'none';
      document.body.appendChild(element);

      element.click();
      document.body.removeChild(element);
    } else {
      console.log("No file")
    }
  } catch (error) {
    console.log(error)
  }
}


export function* saveExportExcelHandler() {
  try {
    const flightSelected = yield select(state => state.flightSelected);
    const flightId = flightSelected?.id;
    const flightName = flightSelected?.name;
    const projectName = yield select(state => state.selectedProject.name);
    const fileName = `${projectName}-${flightName}-excel`;

    const response = yield call(saveExportExcelRequest, flightId);

    // Check if response is successful and contains data
    if (response && response.data) {
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });

      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `${fileName}.xlsx`;
      link.click();
    } else {
      console.error("Invalid or empty response data");
    }
  } catch (error) {
    console.error("Error in saveExportExcelHandler:", error);
  }
}






